import { Combobox } from "@dashboard/components/Combobox";
import FormSpacer from "@dashboard/components/FormSpacer";
import useCollectionSearch from "@dashboard/searches/useCollectionSearch";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { sectionMessages } from "../../messages";

interface WsmCollectionFieldProps {
  value: string;
  /**
   * A collection picked before this screen opened, so the field shows its name
   * without waiting for a search that may never be typed.
   */
  knownCollection?: { id: string; name?: string | null } | null;
  disabled?: boolean;
  error?: string;
  onChange: (collectionId: string) => void;
}

/** Both create screens key their row on a collection, so both pick one here. */
export const WsmCollectionField = ({
  value,
  knownCollection,
  disabled,
  error,
  onChange,
}: WsmCollectionFieldProps) => {
  const intl = useIntl();
  /**
   * What the merchant picked, remembered here. The options list is only ever the
   * CURRENT search, so without this the chosen collection disappears out of the
   * field the moment the merchant types another query - the value is still set,
   * the screen just stops showing it.
   */
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(null);
  const { search, result } = useCollectionSearch({
    variables: { after: null, first: 20, query: "" },
  });
  const searched = (mapEdgesToItems(result.data?.search) ?? []).map(collection => ({
    label: collection.name,
    value: collection.id,
  }));
  const seed = picked?.id === value ? picked : knownCollection;
  const options =
    seed?.id && !searched.some(option => option.value === seed.id)
      ? [{ label: seed.name || seed.id, value: seed.id }, ...searched]
      : searched;

  return (
    <>
      <Combobox
        name="collection"
        label={intl.formatMessage(sectionMessages.collection)}
        data-test-id="wsm-collection-field"
        disabled={disabled}
        options={options}
        value={options.find(option => option.value === value) ?? null}
        loading={result.loading}
        fetchOptions={search}
        onChange={event => {
          const option = event.target.value
            ? options.find(candidate => candidate.value === event.target.value)
            : null;

          setPicked(option ? { id: option.value, name: option.label } : null);
          onChange(event.target.value ?? "");
        }}
        error={!!error}
        helperText={error ?? intl.formatMessage(sectionMessages.collectionHelp)}
        alwaysFetchOnFocus
      />
      <FormSpacer />
    </>
  );
};

export default WsmCollectionField;
